"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Loader, Send } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
export default function GetInTouch() {
  return <section data-section-id="41275" id="get-a-quote" className="relative w-full bg-background dark text-foreground overflow-hidden">
      <div className="flex flex-col lg:flex-row min-h-screen">
        
        <div className="relative w-full lg:w-1/2 p-8 lg:p-16 flex flex-col justify-center z-10 bg-[url('https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&q=80')] bg-cover bg-center">
          
          <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20 bg-black/40">
            <svg className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[150%] h-[150%] text-primary" viewBox="0 0 100 100" fill="none" stroke="currentColor" strokeWidth="0.5">
              <path d="M0 50 Q 25 25, 50 50 T 100 50" />
              <path d="M0 40 Q 25 15, 50 40 T 100 40" />
              <path d="M0 60 Q 25 35, 50 60 T 100 60" />
              <circle cx="20" cy="50" r="2" />
              <circle cx="50" cy="20" r="3" />
              <circle cx="80" cy="80" r="4" />
              <line x1="20" y1="50" x2="50" y2="20" />
              <line x1="50" y1="20" x2="80" y2="80" />
            </svg>
          </div>

          <div className="relative z-20 max-w-xl mx-auto lg:mx-0">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-default text-primary-foreground mb-6 leading-tight tracking-tight">
              Crafting <span className="text-primary">Architectural Landscapes</span>
            </h2>
            <p className="text-lg font-sans max-w-md !text-[hsl(210,17%,98%)] md:text-xl">Expert outdoor design and maintenance for Rockland and Orange County. Transform your property with our meticulous care and sustainable practices.</p>
          </div>
        </div>

        
        <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-border to-transparent opacity-50 relative z-20" />

        
        <div className="w-full p-4 flex items-center justify-center !bg-[hsl(88,61%,20%)] lg:w-1/2 md:p-8 lg:p-16">
          <Card className="text-card-foreground flex flex-col gap-6 py-6 w-full max-w-xl border-border/40 shadow-2xl relative z-20 rounded-sm !bg-[hsl(88,61%,20%)]">
            <CardHeader className="space-y-2 pb-8">
              <CardTitle className="text-2xl md:text-3xl font-default text-foreground">
                Get a Quote
              </CardTitle>
              <CardDescription className="text-muted-foreground font-sans">
                Share your project vision with our landscaping specialists for a personalized consultation.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <FormProvider formKey="quote_form_services" formId="quote_form_services" sectionName="get-a-quote" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              company: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              full_name: {
                required: "Full Name is required"
              },
              email: {
                required: "Email is required",
                pattern: {
                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                  message: "Invalid email address"
                }
              },
              inquiry_type: {
                required: "Please select a service category"
              },
              message: {
                required: "Project details are required"
              }
            }} submitText="Request a Quote" successMessage="Thank you. Our landscaping specialists will review your request and contact you shortly.">
                <ContactForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-primary/10 border border-primary/30 rounded-sm text-center animate-in fade-in zoom-in duration-300">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/20 text-primary mb-4">
          <Send className="w-8 h-8" />
        </div>
        <h3 className="text-xl font-default text-foreground mb-2">Request Sent</h3>
        <p className="text-muted-foreground font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="bg-muted/20 border-border/30 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary font-sans h-12 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="bg-muted/20 border-border/30 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary font-sans h-12 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(845) 555-0123" {...field} className="bg-muted/20 border-border/30 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary font-sans h-12 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/90 font-sans">Property Address</FormLabel>
              <FormControl>
                <Input placeholder="123 Maple Ave, New City" {...field} className="bg-muted/20 border-border/30 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary font-sans h-12 rounded-sm" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans">Service Category</FormLabel>
            <Select onValueChange={field.onChange} value={field.value}>
              <FormControl>
                <SelectTrigger className="bg-muted/20 border-border/30 text-foreground focus:border-primary focus:ring-primary font-sans h-12 rounded-sm w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="Garden Planning">Garden Planning</SelectItem>
                <SelectItem value="Hardscape Installation">Hardscape Installation</SelectItem>
                <SelectItem value="Lawn Care">Lawn Care</SelectItem>
                <SelectItem value="Seasonal Clean-ups">Seasonal Clean-ups</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/90 font-sans">Project Details</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Tell us about your project or maintenance needs..." className="bg-muted/20 border-border/30 text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:ring-primary font-sans min-h-[120px] rounded-sm resize-y" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-sm">
          <p className="text-destructive text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 text-lg bg-primary text-primary-foreground hover:bg-[#0a1a12] hover:text-white rounded-sm font-default tracking-wide transition-colors duration-300">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending...
          </> : "Request a Quote"}
      </Button>
    </Form>;
}